import { useQuery } from "@tanstack/react-query";
import { BadgeCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Props {
  path?: string | null;
  name?: string | null;
  verified?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

const sizes = {
  sm: { box: "h-9 w-9 text-xs", badge: "h-3.5 w-3.5 -right-0.5 -bottom-0.5" },
  md: { box: "h-12 w-12 text-sm", badge: "h-4 w-4 -right-0.5 -bottom-0.5" },
  lg: { box: "h-16 w-16 text-base", badge: "h-5 w-5 -right-1 -bottom-1" },
  xl: { box: "h-24 w-24 text-xl", badge: "h-7 w-7 -right-1 -bottom-1" },
};

function initials(name?: string | null) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");
}

export function FreelancerAvatar({ path, name, verified, size = "md", className }: Props) {
  const s = sizes[size];
  const { data: url } = useQuery({
    queryKey: ["avatar-url", path],
    enabled: !!path,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from("avatars")
        .createSignedUrl(path!, 60 * 60 * 2);
      if (error) throw error;
      return data.signedUrl;
    },
  });

  return (
    <div className={cn("relative inline-block shrink-0", className)}>
      <div
        className={cn(
          "grid place-items-center overflow-hidden rounded-full border border-border bg-muted font-medium text-muted-foreground",
          s.box,
        )}
      >
        {url ? (
          <img src={url} alt={name ?? "Avatar"} className="h-full w-full object-cover" />
        ) : (
          <span>{initials(name)}</span>
        )}
      </div>
      {verified && (
        <span
          className={cn(
            "absolute grid place-items-center rounded-full bg-background ring-2 ring-background",
            s.badge,
          )}
          title="Identity verified"
          aria-label="Identity verified"
        >
          <BadgeCheck className="h-full w-full fill-primary text-primary-foreground" />
        </span>
      )}
    </div>
  );
}
